import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';

// Core
import { appConfig } from '../../config/app-config';
import { ErrorMessage } from '../../core/model/error-message';
import { IBoolResult, BoolResult } from '../../core/interfaces/bool-result';
import { IObjectResult, ObjectResult } from '../../core/interfaces/object-result';
import { DataHubService } from '../../core/datahub/datahub.service';

// user
import { User } from '../../user/model/user';
import { userConfig } from '../config/user.config';
import { UiMessageSnackService } from '../../core/components/message-snack/service/message-snack.service';

@Injectable()
export class UserDatahubService extends DataHubService {

   constructor(
      http: HttpClient,
      log: UiMessageSnackService,
   ) {
      super(http, log);
   }

   /**
    * signIn
    * @param account Account of user to sign in.
    * @param password Passwort of user to sign in.
    * @param result User[] to take user data from backend. Only one object is expected.
    * @param debugMode if true dump certain info to console
    * @returns Promise<IBoolResult>
    */
   public signIn(
      account: string,
      password: string,
      result: User[],                  // output
      debugMode: boolean = false
   ): Promise<IBoolResult> {
      if (debugMode) {console.log('[UserDatahubService]:userSignIn(called)'); }

      // request params setup
      const __params = new HttpParams();

      const prom = new Promise<IBoolResult>((resolve, reject) => {

         // -- mocking case ---------------------------------------------------
         if (appConfig.dev.mockingMode) {
            const filepath = `${appConfig.dev.mockingDataFolder}/user.json`;

            // log
            if (appConfig.dev.debugMode) {
               console.warn('Mocking HTTP request: ', userConfig.urlUserLogin);
            }

            // -->>ASY1
            this.readFileMapJsonObjectToClass<User>(
               filepath,
               'user',
               result,
               User.jsObjToClass,
               debugMode
            )
            .then((boolResultASY1: IBoolResult) => {
               // check account/password
               if (account === appConfig.test.account && password === appConfig.test.passwort) {
                  resolve(boolResultASY1);
                  return;
               }
               // <<-- account data wrong
               resolve(BoolResult.createBoolResult2_false('user/USER_UNKNOWN', 'Benutzer nicht bekannt'));
            })
            .catch((err: ErrorMessage) => { reject(err); });
            // finish mocking case
            return;
         }

         // -- HTTP case ------------------------------------------------------
         this.httpPostAsJsonWithParams(
            userConfig.urlUserLogin,
            __params,
            {
               username: account,
               password: password
            },
            debugMode
         )
         .then((data: any) => {

            if (data['username'] && typeof data['username'] === 'string') {
               const user = new User(
                  'UserID',
                  'Benutzername',
                  data['username'],
                  'Nachname',
                  'Firstname',
                  'username@irgendwo.de',
                  'DBKey'
               );
               result.push(user);

               // send ok
               resolve(BoolResult.createBoolResult_true());
               return;
            }

            // account data wrong
            console.warn('Benutzer nicht bekannt');
            resolve(BoolResult.createBoolResult2_false('user/USER_UNKNOWN', 'Benutzer nicht bekannt'));
         })
         .catch((err: ErrorMessage) => {
            if ((err.errorObject as HttpErrorResponse).status === 401) {
               resolve(BoolResult.createBoolResult2_false('user/USER_UNKNOWN', 'Login fehlgeschlagen!'));
            } else {
               reject(new ErrorMessage('user/USER_INTERNAL_ERROR', 'Interner Fehler'));
            }
         });
      });
      return prom;
   }

   /**
    * signOut
    * @param user User to sign out.
    * @param debugMode if true dump certain info to console
    * @returns Promise<IBoolResult>
    */
   public signOut(
      user: User,
      debugMode: boolean = false
   ): Promise<IBoolResult> {
      if (debugMode) {console.log('[UserDatahubService]:signOut(called)'); }

      const prom = new Promise<IBoolResult>((resolve, reject) => {

         // mocking case
         if (appConfig.dev.mockingMode) {
            // -> ok
            resolve(BoolResult.createBoolResult_true());
            return;
         }

         // HTTP case
         reject(new ErrorMessage('user/USER_INTERNAL_ERROR', 'Interner Fehler'));
      });
      return prom;
   }


   // ---------------------------------------------------------------------------------------------
   // --- Backend version
   // ---------------------------------------------------------------------------------------------
   public loadBackendVersion(): Promise<any> {

      // request params setup
      const __params = new HttpParams();
      const __url = userConfig.urlDataBackendVersion;

      // mocking case
      if (appConfig.dev.mockingMode) {
         // log
         console.warn('Mocking HTTP request: ', __url);

         return new Promise<any>( (resolve, reject) => {
            resolve('Backend Version:xyz');
         });
      }

      // regular HTTP case
      const prom = new Promise<any>((resolve, reject) => {

         this.httpGetAsJsonWithParams(
            __url,
            __params,
            userConfig.debugMode
         )
         .then((data: any) => {

            // send data
            resolve(data);
         })
         .catch((err: ErrorMessage) => {
            reject(new ErrorMessage('user/USER_INTERNAL_ERROR', 'Netzwerk Fehler'));
         });

      });
      return prom;
   }

    public getUserRoles(): Promise<any> {
        // request params setup
        const __params = new HttpParams();
        const __url = userConfig.urlUserRoles;
        // mocking case
        if (appConfig.dev.mockingMode) {
            // log
            console.warn('Mocking HTTP request: ', __url);
            return new Promise<any>((resolve, reject) => {
                resolve(['WRITE']);
            });
        }
        // regular HTTP case
        const prom = new Promise<any>((resolve, reject) => {
            this.httpGetAsJsonWithParams(
                __url,
                __params,
                userConfig.debugMode
            )
                .then((data: any) => {
                    // send data
                    resolve(data);
                })
                .catch((err: ErrorMessage) => {
                    reject(new ErrorMessage('user/USER_INTERNAL_ERROR', 'Netzwerk Fehler'));
                });
        });
        return prom;
    }

    public changePassword(password: string): Promise<IBoolResult> {
        const __params = new HttpParams();

        const prom = new Promise<IBoolResult>((resolve, reject) => {

            // -- HTTP case ------------------------------------------------------
            this.httpPutAsJsonWithParams(
                userConfig.urlPasswordChange,
                __params,
                {
                    password: password
                },
                false
            )
                .then((data: any) => {
                    resolve(BoolResult.createBoolResult_true());
                    return;
                })
                .catch((err: ErrorMessage) => {
                    reject(new ErrorMessage('user/USER_INTERNAL_ERROR', 'Netzwerk Fehler'));
                });
        });
        return prom;
    }

}



// WEBPACK FOOTER //
// /builds/cp/frap/t3/planer/src/app/user/service/user-datahub.service.ts